import { useEffect, useRef } from 'react';

import type { ClientStorageItem } from '@/shared/api/clientStorage';

import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { User } from '@/shared/types/User';

import type { TrialVariant, TrialVariantState } from '../lib/types';

import { useCognitoCheckUser } from '../../currentUser/model/useCognitoCheckUser';
import { useCurrentUser } from '../../currentUser/model/useCurrentUser';
import { computeTrialVariant } from '../lib/computeTrialVariant';
import { TRIAL_VARIANT_KEY, TRIAL_VARIANT_QUERY_KEY } from '../lib/constants';
import { isCreatedDuringTrialExperiment, isExcludedFromTrialVariant } from '../lib/eligibility';
import { isTrialVariant } from '../lib/types';
import { persistTrialVariant } from './persistTrialVariant';
import { useTrialVariantStore } from './useTrialVariantStore';

const LOADING: TrialVariantState = { status: 'loading', variant: null };
const RESOLVED_NONE: TrialVariantState = { status: 'resolved', variant: null };
const RESOLVED_C: TrialVariantState = { status: 'resolved', variant: 'C' };
const RESOLVED_D: TrialVariantState = { status: 'resolved', variant: 'D' };

const resolved = (variant: null | TrialVariant) => {
  if (variant === 'C') return RESOLVED_C;
  if (variant === 'D') return RESOLVED_D;
  return RESOLVED_NONE;
};

const fetchTrialVariantItem = async (): Promise<ClientStorageItem | null> => {
  try {
    return await API.getItem(TRIAL_VARIANT_KEY);
  } catch (error) {
    if ((error as { status?: number }).status === 404) return null;
    throw error;
  }
};

type TrialVariantUser = Pick<User, 'country' | 'created_date' | 'id'>;

const selectTrialVariantUser = ({ id, country, created_date }: User): TrialVariantUser => ({ id, country, created_date });

/**
 * The assignment of a user who should have one but whose Client Storage item is missing or unreadable (the signup
 * write failed, or the item can't be read): the variant is deterministic for the user id and country, so it is
 * recomputed with the same inputs as at signup. Users created before the experiment and excluded users stay outside.
 */
const getFallbackVariant = (user: TrialVariantUser | undefined): null | TrialVariant => {
  if (!user || isExcludedFromTrialVariant() || !isCreatedDuringTrialExperiment(user.created_date)) return null;

  return computeTrialVariant(user.id, user.country);
};

export const useTrialVariant = (): TrialVariantState => {
  const assignment = useTrialVariantStore((state) => state.assignment);

  const cognito = useCognitoCheckUser();
  const isAuthenticated = Boolean(cognito.data?.isAuthorized);
  const { data: user, isLoading: isUserLoading } = useCurrentUser(selectTrialVariantUser);
  const userId = user?.id;
  const queryClient = useQueryClient();

  const freshVariant = assignment && assignment.userId === userId ? assignment.variant : null;
  const isStorageReadEnabled = !freshVariant && isAuthenticated && Boolean(userId);

  const query = useQuery({
    enabled: isStorageReadEnabled,
    queryFn: fetchTrialVariantItem,
    queryKey: TRIAL_VARIANT_QUERY_KEY,
    retry: (failureCount, error) => (error as { status?: number }).status !== 404 && failureCount < 2,
    staleTime: Infinity,
  });

  const readErrorReported = useRef(false);

  useEffect(() => {
    if (!query.isError || readErrorReported.current) return;

    readErrorReported.current = true;
    Sentry.captureException(query.error, { tags: { feature: 'trialVariant', phase: 'read' } });
  }, [query.error, query.isError]);

  const storedValue = query.data?.value;
  const storedVariant = isTrialVariant(storedValue) ? storedValue : null;
  const isStorageReadDone = query.isSuccess || query.isError;
  const fallbackVariant =
    isStorageReadDone && !freshVariant && !storedVariant && isAuthenticated ? getFallbackVariant(user) : null;

  // Best effort, once per user: store the recomputed assignment so the backend and later sessions find it. The variant is
  // stable for a user id + server-side country, so overwriting an unreadable item writes the same value.
  const resavedForUserId = useRef<null | string>(null);
  const shouldResave = Boolean(fallbackVariant);

  useEffect(() => {
    if (!shouldResave || !fallbackVariant || !userId || resavedForUserId.current === userId) return;

    resavedForUserId.current = userId;
    void persistTrialVariant(queryClient, fallbackVariant, 'resave');
  }, [fallbackVariant, queryClient, shouldResave, userId]);

  if (cognito.isLoading) return LOADING;

  if (!isAuthenticated) return RESOLVED_NONE;

  if (isUserLoading || !userId) return LOADING;

  if (freshVariant) return resolved(freshVariant);

  if (query.isLoading) return LOADING;

  return resolved(storedVariant ?? fallbackVariant);
};
